import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { FormSnippetGroup } from '@ipfabric/ui-components/utils/formSnippets/FormSnippetGroup';
import type { FormCompositionHook } from '@ipfabric/ui-components/utils/formSnippets/useFormComposition';
import { safeAbsurd } from '@ipfabric/utils';
import type { ReactNode } from 'react';
import { useWatch } from 'react-hook-form';
import type { DiscoverySettingsObject } from '../../../model/DiscoverySettingsObject';
import type { storedDeviceCredentials } from '../../../snippets/storedDeviceCredentials';
import type { storedPrivilegedCredentials } from '../../../snippets/storedPrivilegedCredentials';
import type { storedVendorsApi } from '../../../snippets/storedVendorsApi';
import { useAuthenticationValidationContext } from '../context/AuthenticationValidationContext';
import { DeviceCredentialsSubTab } from './DeviceCredentialsSubTab';
import { PrivilegedCredentialsSubTab } from './PrivilegedCredentialsSubTab';
import { VendorsApiSubTab } from './VendorsApiSubTab';

export type AuthenticationSubTab =
  | 'deviceCredentials'
  | 'vendors'
  | 'privilegedCredentials';

type Props = {
  formComposition: FormCompositionHook<
    'deviceCredentials' | 'privilegedCredentials' | 'vendors',
    {
      deviceCredentials: FormSnippetGroup<
        DiscoverySettingsObject,
        'deviceCredentials',
        unknown,
        { deviceCredentials: typeof storedDeviceCredentials }
      >;
      privilegedCredentials: FormSnippetGroup<
        DiscoverySettingsObject,
        'privilegedCredentials',
        unknown,
        { privilegedCredentials: typeof storedPrivilegedCredentials }
      >;
      vendors: FormSnippetGroup<
        DiscoverySettingsObject,
        'vendors',
        unknown,
        { vendors: typeof storedVendorsApi }
      >;
    }
  >;

  subTab: AuthenticationSubTab;
  setSubTab: (subTab: AuthenticationSubTab) => void;
};

export const AuthenticationTab = ({
  formComposition,
  subTab,
  setSubTab,
}: Props) => {
  const storedDeviceCredentials = useWatch({
    control: formComposition.form.control,
    name: 'credentials',
  });
  const privilegedDeviceCredentials = useWatch({
    control: formComposition.form.control,
    name: 'privileges',
  });
  const storedVendorAPIs = useWatch({
    control: formComposition.form.control,
    name: 'vendors',
  });

  const deviceCredentialsCount = storedDeviceCredentials.length;
  const privilegedCredentialsCount = privilegedDeviceCredentials.length;
  const vendorAPIsCount = storedVendorAPIs.length;

  const authenticationValidation = useAuthenticationValidationContext();

  /**
   * Creates a badge with tooltip for a tab based on count and validation state
   */
  const renderDeviceCredentialsBadge = (): ReactNode | undefined => {
    const tooltipContent = authenticationValidation.hasError
      ? authenticationValidation.errorLong
      : authenticationValidation.hasWarning
        ? authenticationValidation.warningLong
        : undefined;

    const badgeColor = authenticationValidation.hasError
      ? 'error'
      : authenticationValidation.hasWarning
        ? 'warning'
        : 'neutral';

    return (
      <Tooltip content={tooltipContent}>
        <Badge color={badgeColor} variant='plain'>
          {deviceCredentialsCount}
        </Badge>
      </Tooltip>
    );
  };

  const renderVendorAPIsBadge = (): ReactNode | undefined => {
    const tooltipContent = authenticationValidation.hasError
      ? authenticationValidation.errorLong
      : undefined;

    const badgeColor = authenticationValidation.hasError ? 'error' : 'neutral';

    return (
      <Tooltip content={tooltipContent}>
        <Badge color={badgeColor} variant='plain'>
          {vendorAPIsCount}
        </Badge>
      </Tooltip>
    );
  };

  const tabs = [
    {
      id: 'deviceCredentials' as const,
      label: 'Device Credentials',
      badge: renderDeviceCredentialsBadge(),
    },
    {
      id: 'vendors' as const,
      label: 'Vendor APIs',
      badge: renderVendorAPIsBadge(),
    },
    {
      id: 'privilegedCredentials' as const,
      label: 'Enable mode passwords',
      badge: (
        <Badge color='neutral' variant='plain'>
          {privilegedCredentialsCount}
        </Badge>
      ),
    },
  ];

  return (
    <div className='flex flex-col flex-1 drop-shadow'>
      <Tabs tabs={tabs} value={subTab} onChange={setSubTab} variant='outer' />
      <div className='flex flex-1 w-full'>
        {(() => {
          switch (subTab) {
            case 'deviceCredentials':
              return (
                <DeviceCredentialsSubTab formComposition={formComposition} />
              );
            case 'privilegedCredentials':
              return (
                <PrivilegedCredentialsSubTab
                  formComposition={formComposition}
                />
              );
            case 'vendors':
              return <VendorsApiSubTab formComposition={formComposition} />;
            default:
              safeAbsurd(subTab, 'Unknown Authentication subtab');
              return <p>Error: unknown subtab</p>;
          }
        })()}
      </div>
    </div>
  );
};
